import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import Panel from "../../components/Panel";

/*
 * One column of the live board. Fixed height so the three columns line up and
 * the page itself never grows: each list scrolls inside its own panel, and
 * says so when there is more below the fold.
 */

export const NARROW_BOARD_QUERY = "(max-width: 1100px)";

const COLUMN_HEIGHT = "clamp(460px, calc(100vh - 320px), 760px)";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: COLUMN_HEIGHT,
    [`@media ${NARROW_BOARD_QUERY}`]: { height: "auto", minHeight: 320 },
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "16px 20px 10px",
    flexShrink: 0,
  },
  eyebrow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: T.subtleFg,
    fontWeight: 600,
  },
  dot: { width: 8, height: 8, borderRadius: 999, background: T.primary },
  line: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minWidth: 0,
  },
  countWrap: { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 },
  count: {
    fontSize: 28,
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: "-0.01em",
    color: T.fg,
    fontVariantNumeric: "tabular-nums",
  },
  noun: { fontSize: 14, color: T.mutedFg, whiteSpace: "nowrap" },
  side: { display: "flex", alignItems: "center", gap: 12, flexShrink: 0 },
  meta: { fontSize: 12, color: T.mutedFg, textAlign: "right" },
  toolbar: { flexShrink: 0 },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    [`@media ${NARROW_BOARD_QUERY}`]: { overflowY: "visible" },
  },
  empty: {
    padding: "28px 20px",
    textAlign: "center",
    color: T.subtleFg,
    fontSize: 13,
    borderTop: `1px solid ${T.borderSoft}`,
  },
  more: {
    flexShrink: 0,
    padding: "6px 20px 8px",
    fontSize: 12,
    color: T.mutedFg,
    borderTop: `1px solid ${T.borderSoft}`,
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    [`@media ${NARROW_BOARD_QUERY}`]: { display: "none" },
  },
  moreLink: {
    border: "none",
    background: "transparent",
    color: T.primary,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    padding: 0,
  },
});

const ROW_SELECTOR = "li[data-row]";

function hiddenRows(element) {
  const rows = Array.from(element.querySelectorAll(ROW_SELECTOR));
  const top = element.scrollTop;
  const bottom = top + element.clientHeight;

  return {
    below: rows.filter((row) => row.offsetTop + row.offsetHeight > bottom + 1)
      .length,
    above: rows.filter((row) => row.offsetTop + row.offsetHeight <= top).length,
  };
}

export default function ColumnPanel({
  title,
  count,
  noun,
  meta,
  action,
  live = false,
  hideCount = false,
  toolbar,
  itemCount,
  empty,
  children,
}) {
  const s = useStyles();
  const bodyRef = useRef(null);
  const [hidden, setHidden] = useState({ below: 0, above: 0 });

  const measure = useCallback(() => {
    const element = bodyRef.current;
    if (!element) return;

    const next =
      element.scrollHeight > element.clientHeight + 1
        ? hiddenRows(element)
        : { below: 0, above: 0 };

    setHidden((current) =>
      current.below === next.below && current.above === next.above
        ? current
        : next,
    );
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, itemCount, children]);

  const scrollToEnd = () => {
    const element = bodyRef.current;
    if (element) {
      element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
    }
  };

  return (
    <Panel className={s.panel}>
      <div className={s.header}>
        <div className={s.line}>
          <div className={s.eyebrow}>
            {live && <span className={s.dot} aria-hidden="true" />}
            {title}
          </div>
          {hideCount && (
            <div className={s.side}>
              {meta && <div className={s.meta}>{meta}</div>}
              {action}
            </div>
          )}
        </div>

        {!hideCount && (
          <div className={s.line}>
            <div className={s.countWrap}>
              <span className={s.count}>{count}</span>
              <span className={s.noun}>{noun}</span>
            </div>
            <div className={s.side}>
              {meta && <div className={s.meta}>{meta}</div>}
              {action}
            </div>
          </div>
        )}
      </div>

      {toolbar && <div className={s.toolbar}>{toolbar}</div>}

      <div ref={bodyRef} className={s.body} onScroll={measure}>
        {itemCount === 0 ? <div className={s.empty}>{empty}</div> : children}
      </div>

      {(hidden.below > 0 || hidden.above > 0) && (
        <div className={s.more} role="status" aria-live="polite">
          <span>
            {hidden.below > 0
              ? `${hidden.below} more below`
              : `${hidden.above} above`}
          </span>
          {hidden.below > 0 && (
            <button type="button" className={s.moreLink} onClick={scrollToEnd}>
              Scroll to end
            </button>
          )}
        </div>
      )}
    </Panel>
  );
}
